import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {FileReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {NullMessageId} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useChatStateLens} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback} from 'react'

import type {File} from '../components/ContentPreview/content-preview-types'
import {useContentPreview} from '../components/ContentPreview/ContentPreviewContext'

const BLOB_PATH_REGEX = /^\/([^/]+)\/([^/]+)\/blob\/(.+)$/
const TREE_PATH_REGEX = /^\/([^/]+)\/([^/]+)\/tree\/(.+)$/
const REPO_PATH_REGEX = /^\/([^/]+)\/([^/]+)\/?$/

interface ParsedGitHubFileUrl {
  owner: string
  repo: string
  refAndPath: string
}

interface ParsedGitHubTreeUrl {
  owner: string
  repo: string
  refAndPath: string
}

interface ParsedGitHubRepoUrl {
  owner: string
  repo: string
}

/**
 * Parses a same-origin GitHub blob URL into its components.
 * Works for any GitHub instance (github.com, GHES, localhost) by checking
 * the link's origin against `window.location.origin`.
 */
function parseGitHubFileUrl(href: string): ParsedGitHubFileUrl | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    const match = url.pathname.match(BLOB_PATH_REGEX)
    if (!match) return null
    return {
      owner: match[1]!,
      repo: match[2]!,
      refAndPath: decodeURIComponent(match[3]!),
    }
  } catch {
    return null
  }
}

/**
 * Parses a same-origin GitHub repo URL (e.g. /owner/repo) into its components.
 */
function parseGitHubRepoUrl(href: string): ParsedGitHubRepoUrl | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    const match = url.pathname.match(REPO_PATH_REGEX)
    if (!match) return null
    return {
      owner: match[1]!,
      repo: match[2]!,
    }
  } catch {
    return null
  }
}

/**
 * Parses a same-origin GitHub tree URL (e.g. /owner/repo/tree/ref/path) into its components.
 */
function parseGitHubTreeUrl(href: string): ParsedGitHubTreeUrl | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    const match = url.pathname.match(TREE_PATH_REGEX)
    if (!match) return null
    return {
      owner: match[1]!,
      repo: match[2]!,
      refAndPath: decodeURIComponent(match[3]!),
    }
  } catch {
    return null
  }
}

/**
 * Fetches the list of branch names for a repository via the refs endpoint.
 * Returns an empty array on failure.
 */
async function fetchBranchNames(owner: string, repo: string): Promise<string[]> {
  try {
    const response = await fetch(`/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/refs?type=branch`, {
      headers: {Accept: 'application/json'},
    })
    if (!response.ok) return []
    const data = (await response.json()) as {refs?: string[]}
    return data.refs ?? []
  } catch {
    return []
  }
}

/**
 * Given a combined ref+path string (e.g. "feature/foo/src/components") and a
 * list of known branch names, finds the longest matching branch name and splits
 * the string into ref and directory path. Returns null if no match is found.
 */
function resolveRefAndPath(refAndPath: string, branches: string[]): {ref: string; dirPath: string} | null {
  // Sort branches by length descending so we greedily match the longest ref first
  const sorted = branches.slice().sort((a, b) => b.length - a.length)
  for (const branch of sorted) {
    if (refAndPath === branch) {
      return {ref: branch, dirPath: ''}
    }
    if (refAndPath.startsWith(`${branch}/`)) {
      return {ref: branch, dirPath: refAndPath.slice(branch.length + 1)}
    }
  }
  return null
}

/**
 * Returns a click handler that intercepts clicks on GitHub file links within
 * markdown-rendered chat messages and opens the file in the ResourcePanel /
 * ContentPreview instead of navigating away.
 *
 * In embedded (assistive) mode, links navigate normally.
 */
export function useOnGitHubFileLinkClick() {
  const manager = useChatManager()
  const {
    openPreviewPane,
    openEphemeralItem,
    updateItem,
    openResourcePanel,
    setResourcePanelRepoId,
    setResourcePanelExpandedPath,
  } = useContentPreview()
  const isEmbedded = useChatStateLens(s => s.mode === 'assistive')

  return useCallback(
    (event: MouseEvent | React.MouseEvent<HTMLElement>) => {
      if (!copilotFeatureFlags.copilotResourcePanel) return
      if (isEmbedded) return

      const target = (event.target as HTMLElement).closest('a')
      if (!target) return

      // Allow links explicitly marked for new-tab navigation to use default browser behavior
      if (target.hasAttribute('data-open-in-new-tab')) return

      const href = target.getAttribute('href')
      if (!href) return

      const parsedFile = parseGitHubFileUrl(href)
      const parsedTree = parsedFile ? null : parseGitHubTreeUrl(href)
      const parsedRepo = parsedFile || parsedTree ? null : parseGitHubRepoUrl(href)

      if (!parsedFile && !parsedTree && !parsedRepo) return

      event.preventDefault()

      void (async () => {
        try {
          const owner = parsedFile?.owner ?? parsedTree?.owner ?? parsedRepo!.owner
          const repo = parsedFile?.repo ?? parsedTree?.repo ?? parsedRepo!.repo
          const nwo = `${owner}/${repo}`

          // Look up the repo by NWO to get the repoID
          const results = await manager.service.fetchRepositoriesSearch(nwo, 5)
          const matchedRepo = results.find(r => r.nwo.toLowerCase() === nwo.toLowerCase())

          if (!matchedRepo) {
            // Repo not found — fall back to navigation
            window.open(href, '_blank', 'noopener,noreferrer')
            return
          }

          const repoID = matchedRepo.databaseId

          let resolvedRef = ''

          if (parsedFile) {
            // Branch names may contain slashes, so try each possible ref/path split
            const segments = parsedFile.refAndPath.split('/')
            let resolvedPath = ''
            let resolvedContent = ''
            let resolvedLanguage = 'plaintext'

            let found = false
            for (let i = 1; i < segments.length; i++) {
              const candidateRef = segments.slice(0, i).join('/')
              const candidatePath = segments.slice(i).join('/')

              const result = await manager.service.fetchFileContentsNwo(
                parsedFile.owner,
                parsedFile.repo,
                candidatePath,
                candidateRef,
              )

              if (result.ok && result.payload) {
                resolvedRef = candidateRef
                resolvedPath = candidatePath
                resolvedContent = result.payload.content
                resolvedLanguage = result.payload.language ?? 'plaintext'
                found = true
                break
              }
            }

            if (!found) {
              window.open(href, '_blank', 'noopener,noreferrer')
              return
            }

            const fileRef: FileReference = {
              type: 'file',
              url: href,
              path: resolvedPath,
              repoID,
              repoOwner: parsedFile.owner,
              repoName: parsedFile.repo,
              ref: resolvedRef,
              commitOID: '',
            }

            const newFile: File = {
              messageId: NullMessageId,
              id: `file:${referenceID(fileRef)}#0`,
              timestamp: new Date(),
              isUserEdited: false,
              value: resolvedContent,
              type: 'file',
              name: resolvedPath,
              isStreaming: false,
              language: resolvedLanguage,
              reference: fileRef,
            }

            updateItem(newFile)
            openEphemeralItem(newFile.id)
          }

          let resolvedDirPath: string | undefined
          if (parsedTree) {
            // Resolve ref and directory path from tree URL by fetching the repo's
            // branch list and greedily matching against the refAndPath string.
            const [repoResult, branches] = await Promise.all([
              manager.service.fetchRepo(repoID),
              fetchBranchNames(parsedTree.owner, parsedTree.repo),
            ])

            if (!repoResult.ok || !repoResult.payload) {
              window.open(href, '_blank', 'noopener,noreferrer')
              return
            }

            // Include the default branch in the list in case the refs endpoint
            // didn't return it (e.g. due to pagination or caching)
            const defaultBranch = repoResult.payload.defaultBranch
            const allBranches = branches.includes(defaultBranch) ? branches : [defaultBranch, ...branches]

            const match = resolveRefAndPath(parsedTree.refAndPath, allBranches)
            if (match) {
              resolvedRef = match.ref
              resolvedDirPath = match.dirPath
            } else {
              // No branch matched — fall back to normal navigation
              window.open(href, '_blank', 'noopener,noreferrer')
              return
            }
          }

          // Open the ResourcePanel with the matching repo selected
          setResourcePanelRepoId({
            repoId: repoID,
            ref: parsedFile ? resolvedRef || undefined : parsedTree ? resolvedRef || undefined : undefined,
          })
          setResourcePanelExpandedPath(resolvedDirPath ?? '')
          openResourcePanel()
          openPreviewPane()
          sendEvent('dotcom_chat.activate', {target: 'RESOURCE_PANEL_OPEN', mode: 'immersive', source: 'message_link'})
        } catch {
          // On any unexpected error, fall back to navigation
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      })()
    },
    [
      isEmbedded,
      manager.service,
      openPreviewPane,
      openEphemeralItem,
      updateItem,
      openResourcePanel,
      setResourcePanelRepoId,
      setResourcePanelExpandedPath,
    ],
  )
}
